import { Card } from 'react-bootstrap-5';
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import DownloadCaLink from './download_ca_link';
import { fetchTranslation } from '../../../../helpers/i18n';

// Defined as a function (not a const object) so translations are looked up against the
// current I18n locale at render time rather than frozen at module-load time.
export const stepTexts = () => ({
  'custom_word': {
    step2: fetchTranslation('step2_custom_word'),
    step3: fetchTranslation('step3_custom_word'),
    step3Dragging: fetchTranslation('step3_dragging_custom_word'),
    downloadText: fetchTranslation('download_docx')
  },
  'custom_pdf': {
    step2: fetchTranslation('step2_custom_pdf'),
    step3: fetchTranslation('step3_custom_pdf'),
    step3Dragging: fetchTranslation('step3_dragging_custom_pdf'),
    downloadText: fetchTranslation('download_pdf')
  },
  'both': {
    step2: fetchTranslation('step2_both'),
    step3: fetchTranslation('step3_both'),
    step3Dragging: fetchTranslation('step3_dragging_both')
  }
});

export default function PdfCa({ caFiles, uploadedFile, onFileUploaded }) {
  const onDrop = useCallback((acceptedFiles) => {
    onFileUploaded(acceptedFiles[0]);
  }, []);

  const acceptedFileTypes = caFiles.map((caFile) => {
    if (caFile.type === 'custom_word') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (caFile.type === 'custom_pdf') return 'application/pdf';
    return null;
  }).filter(n => n);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles: 1
  });

  const texts = stepTexts();
  const parseStepText = step => texts[caFiles.length > 1 ? 'both' : caFiles[0].type]?.[step];

  return (
    <React.Fragment>
      <div className="mb-4 text-center">
        <h5 className="mb-2">
          <strong>{fetchTranslation('step_1')}</strong>
        </h5>
        <div>{fetchTranslation('download_ca_link_text')}</div>
        <div className="d-flex justify-content-center mt-2 gap-2">
          {caFiles.map(({ path, type }) => (
            <DownloadCaLink
              href={path}
              key={path}
              label={texts[type].downloadText}
            />
          ))}
        </div>
      </div>
      <div className="mb-4 text-center">
        <h5 className="mb-2">
          <strong>{fetchTranslation('step_2')}</strong>
        </h5>
        <div name="step2">{parseStepText('step2')}</div>
      </div>
      <div className="mb-4 text-center">
        <h5 className="mb-2">
          <strong>{fetchTranslation('step_3')}</strong>
        </h5>
        <Card className="clickable" {...getRootProps()} style={{ borderStyle: 'dashed' }}>
          <Card.Body className="p-4">
            <input {...getInputProps()} />
            {isDragActive && <div name="step3Dragging">{parseStepText('step3Dragging')}</div>}
            {!isDragActive && !uploadedFile && <div name="step3">{parseStepText('step3')}</div>}
            {!isDragActive && uploadedFile && (
              <div>
                <div>{fetchTranslation('uploaded')} <strong>{uploadedFile.name}</strong></div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </React.Fragment>
  );
}

PdfCa.propTypes = {
  caFiles: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['custom_pdf', 'custom_word']).isRequired,
    path: PropTypes.string.isRequired,
  })).isRequired,
  uploadedFile: PropTypes.object,
  onFileUploaded: PropTypes.func.isRequired
};
